import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import type { PickerRow, UidGroupRow } from 'api/canonicalPicker.types'
import {
  CanonicalLevel,
  PickerSliceState,
  selectCanonicalLevel,
  SourceChoice,
  SourceSelection,
} from 'slices/aiSegmentBuilder/picker'
import scopeUidGroupPicks from 'slices/aiSegmentBuilder/picker/scopeUidGroupPicks'

import useRetry from './useRetry'

type RootState = { picker: PickerSliceState }

const isUnscopedVersion = (row: PickerRow): row is UidGroupRow =>
  row.kind === 'uidGroup' && row.productIds === null

/**
 * Looks up every product the versions picked on level 1 belong to, as they
 * are picked, so level 2 offers the children of each. A switch to specific
 * looks its versions up itself, before it lands.
 */
const useScopeVersions = (
  pickerId: number,
  level: CanonicalLevel,
  choice: SourceChoice | null,
  selection: SourceSelection
) => {
  const dispatch = useDispatch()
  const unscopedIds = useSelector(
    (state: RootState) =>
      selectCanonicalLevel(state, pickerId, level).unscopedIds
  )

  const scopeVersions = useCallback(
    async (rows: PickerRow[]) => {
      const versions = rows.filter(isUnscopedVersion)
      if (level !== 'level1' || !choice || versions.length === 0) return
      await dispatch(
        scopeUidGroupPicks({
          pickerId,
          level,
          source: choice.specific,
          versions,
        })
      )
    },
    [choice, dispatch, level, pickerId]
  )

  const unscoped = useMemo(
    () => unscopedIds.flatMap(id => selection.rows[id] ?? []),
    [selection, unscopedIds]
  )
  const scopeUnscoped = useCallback(
    () => scopeVersions(unscoped),
    [scopeVersions, unscoped]
  )
  const { retry: retryScope, isRetrying: isRetryingScope } =
    useRetry(scopeUnscoped)

  return {
    scopeVersions,
    hasUnscoped: unscoped.length > 0,
    retryScope,
    isRetryingScope,
  }
}

export default useScopeVersions
